/* Everything Automotive — Install + Share card
 * Lives at: frontend/src/components/InstallApp.js
 *
 * The Install button is ALWAYS visible so the app can be (re)installed at any time,
 * even after the home-screen / desktop icon is deleted.
 *  - If Chrome has a live install prompt  -> one-tap native install.
 *  - If not (iOS, or just-deleted-not-yet-rearmed) -> opens a short visual guide.
 * Reads the prompt captured early in src/index.js (window.__deferredInstallPrompt).
 *
 * INSTALL FEEDBACK: on phones the native install gives no visible "in progress"
 * sign, so users think nothing is happening. After they accept the prompt we
 * show an "Installing…" spinner + helper line until the browser fires
 * `appinstalled` (or a safety timeout elapses).
 */

import React, { useState, useEffect, useRef } from "react";
import ShareApp from "./ShareApp";

export default function InstallApp() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [installing, setInstalling] = useState(false);

  const installTimerRef = useRef(null);

  useEffect(() => {
    setIsStandalone(
      window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );

    if (window.__deferredInstallPrompt) {
      setDeferredPrompt(window.__deferredInstallPrompt);
    }

    const onInstallable = () => {
      if (window.__deferredInstallPrompt) {
        setDeferredPrompt(window.__deferredInstallPrompt);
      }
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      window.__deferredInstallPrompt = null;
      setInstalling(false);
      setIsStandalone(true);
      if (installTimerRef.current) {
        clearTimeout(installTimerRef.current);
        installTimerRef.current = null;
      }
    };

    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);

    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
      if (installTimerRef.current) clearTimeout(installTimerRef.current);
    };
  }, []);

  const handleInstall = async () => {
    if (installing) return; // ignore double taps while installing

    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === "accepted") {
          setInstalling(true);
          setDeferredPrompt(null);
          window.__deferredInstallPrompt = null;
          installTimerRef.current = setTimeout(() => {
            setInstalling(false);
            installTimerRef.current = null;
          }, 90000);
        } else {
          setInstalling(false);
        }
      } catch (err) {
        setInstalling(false);
      }
      return;
    }

    setShowGuide(true);
  };

  if (isStandalone) {
    return (
      <section className="ea-install" aria-label="Share Everything Automotive">
        <style>{cardStyles}</style>
        <img className="ea-install__logo" src="/logo192.png" alt="Everything Automotive logo" />
        <div className="ea-install__body">
          <p className="ea-install__title">Enjoying Everything Automotive?</p>
          <ul className="ea-install__benefits">
            <li>Share it with friends</li>
            <li>Help someone find parts &amp; services</li>
          </ul>
        </div>
        <div className="ea-install__action">
          <ShareApp />
        </div>
      </section>
    );
  }

  return (
    <section className="ea-install" aria-label="Install Everything Automotive">
      <style>{cardStyles}</style>

      <img className="ea-install__logo" src="/logo192.png" alt="Everything Automotive logo" />

      <div className="ea-install__body">
        <p className="ea-install__title">Install Everything Automotive</p>
        <ul className="ea-install__benefits">
          <li>Faster loading</li>
          <li>Works like a real app</li>
          <li>Parts, services &amp; cars in one tap</li>
          <li>No Play Store needed</li>
        </ul>

        {installing && (
          <p className="ea-install__progress" role="status" aria-live="polite">
            Installing… please wait. The Everything Automotive icon will appear
            on your home screen in a few seconds.
          </p>
        )}
      </div>

      <div className="ea-install__action">
        <button
          className="ea-install-btn"
          onClick={handleInstall}
          disabled={installing}
          aria-busy={installing}
        >
          {installing ? (
            <>
              <span className="ea-install-spinner" aria-hidden="true" />
              Installing…
            </>
          ) : (
            "Install App"
          )}
        </button>
        <ShareApp />
      </div>

      {showGuide && (
        <div
          className="ea-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="How to install Everything Automotive"
          onClick={() => setShowGuide(false)}
        >
          <div className="ea-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Install Everything Automotive</h3>

            <h4>On Android (Chrome)</h4>
            <ol>
              <li>Tap the <strong>⋮</strong> menu at the top-right.</li>
              <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
            </ol>

            <h4>On Desktop (Chrome / Edge)</h4>
            <ol>
              <li>Click the <strong>install icon</strong> on the right side of the address bar.</li>
              <li>Or open the <strong>⋮</strong> menu and choose <strong>Install Everything Automotive</strong>.</li>
            </ol>

            <h4>On iPhone / iPad (Safari)</h4>
            <ol>
              <li>Open this site in <strong>Safari</strong>.</li>
              <li>Tap the <strong>Share</strong> button (square with an up arrow).</li>
              <li>Tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
            </ol>

            <button className="ea-modal-close" onClick={() => setShowGuide(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

const cardStyles = `
  .ea-install {
    display: flex;
    align-items: center;
    gap: 20px;
    max-width: 760px;
    margin: 16px auto;
    padding: 20px 22px;
    border-radius: 18px;
    background: linear-gradient(135deg, #08182e 0%, #0f3d78 100%);
    color: #eaf2ff;
    box-shadow: 0 10px 30px rgba(8, 20, 45, 0.35);
    flex-wrap: wrap;
  }
  .ea-install__logo {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    flex-shrink: 0;
    background: #ffffff;
    object-fit: contain;
    padding: 4px;
  }
  .ea-install__body { flex: 1 1 240px; min-width: 200px; text-align: left; }
  .ea-install__title {
    margin: 0 0 4px;
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: 0.2px;
  }
  .ea-install__benefits {
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 0.85rem;
    color: #b9cdf0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
  }
  .ea-install__benefits li::before { content: "✓ "; color: #4da3ff; }
  .ea-install__progress {
    margin: 10px 0 0;
    font-size: 0.85rem;
    line-height: 1.4;
    color: #cfe0ff;
    background: rgba(11, 99, 214, 0.2);
    border: 1px solid rgba(77, 163, 255, 0.45);
    border-radius: 10px;
    padding: 8px 12px;
  }
  .ea-install__action { flex-shrink: 0; display: flex; gap: 10px; flex-wrap: wrap; }
  .ea-install-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    background: #0b63d6;
    color: #fff;
    border: none;
    border-radius: 12px;
    padding: 12px 22px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;
  }
  .ea-install-btn:hover:not(:disabled) { background: #0950ad; transform: translateY(-1px); }
  .ea-install-btn:focus-visible { outline: 3px solid #9cc4ff; outline-offset: 2px; }
  .ea-install-btn:disabled { background: #2a4f9e; cursor: progress; opacity: 0.95; }

  .ea-install-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.45);
    border-top-color: #ffffff;
    border-radius: 50%;
    display: inline-block;
    animation: ea-install-spin 0.7s linear infinite;
  }
  @keyframes ea-install-spin { to { transform: rotate(360deg); } }

  .ea-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(5, 12, 28, 0.72);
    display: flex; align-items: center; justify-content: center;
    padding: 16px; z-index: 100000;
  }
  .ea-modal {
    background: #fff; color: #15243f;
    border-radius: 16px; padding: 24px;
    max-width: 400px; width: 100%;
    text-align: left;
  }
  .ea-modal h3 { margin: 0 0 12px; font-size: 1.1rem; }
  .ea-modal h4 { margin: 16px 0 6px; font-size: 0.95rem; }
  .ea-modal ol { margin: 0 0 8px; padding-left: 20px; line-height: 1.6; font-size: 0.92rem; }
  .ea-modal-close {
    width: 100%; border: none; border-radius: 10px;
    padding: 11px; background: #0b63d6; color: #fff;
    font-weight: 600; cursor: pointer; margin-top: 10px;
  }
  @media (prefers-reduced-motion: reduce) {
    .ea-install-btn, .ea-install-btn:hover { transition: none; transform: none; }
    .ea-install-spinner { animation: none; }
  }
`;
